/**
 * Integrytas Group - Common Security Module (CSM)
 * Page: CSM.SYSTEM.AUDIT_LOG
 *
 * Read-only viewer for csm_audit_log.  Provides:
 *   • Filter bar — date range, action type, entity name, user
 *   • Paginated grid (100 rows per page, server-side via range())
 *   • Expandable row showing the field-level diff (old → new)
 *   • Export to CSV
 *
 * Access is restricted to administrators (mirrors the RLS SELECT policy
 * csm_al_admin_select defined in migration 30).
 */

import { Fragment, useEffect, useState, useMemo, useCallback } from 'react';
import { DateInput } from '../../components/common/DateInput';
import { useTranslation } from 'react-i18next';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { apiDb } from '../../lib/api_client';
import type { AuthSession } from '../../types/auth';
import { SortableHeader } from '../../components/common/SortableHeader';
import { useSort, compareStrings } from '../../lib/use_sort';
import { StatusBadge, type StatusVariant } from '../../components/common/StatusBadge';
import { EmptyState } from '../../components/common/EmptyState';
import { ErrorBanner } from '../../components/common/ErrorBanner';
import { SkeletonRows } from '../../components/common/SkeletonRows';
import { PageHeader } from '../../components/common/PageHeader';
import { ContentCard } from '../../components/common/ContentCard';
import { Download } from 'lucide-react';

const PAGE_SIZE = 100;

const AUDIT_GRID_COLS: GridColumnDef[] = [
  { key: 'log_timestamp', header: 'Timestamp', defaultWidth: 180 },
  { key: 'action_type',   header: 'Action',    defaultWidth: 140 },
  { key: 'entity_name',   header: 'Entity',    defaultWidth: 200 },
  { key: 'record_id',     header: 'Record',    defaultWidth: 90  },
  { key: 'user_email',    header: 'User',      defaultWidth: 200 },
  { key: 'ip_address',    header: 'IP',        defaultWidth: 130 },
];

type ActionType = 'INSERT' | 'UPDATE' | 'DELETE' | 'SECURITY_OVERRIDE' | '';

interface AuditRow {
  audit_log_id:   number;
  log_timestamp:  string;
  user_id:        number | null;
  user_email:     string | null;   // joined from csm_user_master
  ip_address:     string | null;
  action_type:    string;
  entity_name:    string;
  record_id:      string | null;
  old_values:     Record<string, unknown> | null;
  new_values:     Record<string, unknown> | null;
  override_reason: string | null;
}

type SortKey = 'log_timestamp' | 'action_type' | 'entity_name' | 'user_email';

// ---------------------------------------------------------------------------
// Diff display helpers
// ---------------------------------------------------------------------------

const ACTION_META: Record<string, { labelKey: string; label: string; variant: StatusVariant }> = {
  INSERT:            { labelKey: 'audit_log.action.insert',            label: 'Insert',            variant: 'success' },
  UPDATE:            { labelKey: 'audit_log.action.update',            label: 'Update',            variant: 'info' },
  DELETE:            { labelKey: 'audit_log.action.delete',            label: 'Delete',            variant: 'danger' },
  SECURITY_OVERRIDE: { labelKey: 'audit_log.action.security_override', label: 'Security override', variant: 'warning' },
};

function ActionBadge({ action }: { action: string }) {
  const { t } = useTranslation();
  const meta = ACTION_META[action];
  return (
    <StatusBadge
      label={meta ? t(meta.labelKey, meta.label) : action.replace('_', ' ')}
      variant={meta?.variant ?? 'neutral'}
    />
  );
}

function DiffTable({
  oldValues, newValues, action,
}: {
  oldValues: Record<string, unknown> | null;
  newValues: Record<string, unknown> | null;
  action:    string;
}) {
  const { t } = useTranslation();

  if (!oldValues && !newValues) return null;

  const keys = Array.from(new Set([
    ...Object.keys(oldValues ?? {}),
    ...Object.keys(newValues ?? {}),
  ])).sort();

  return (
    <table className="w-full border-collapse text-csm-sm font-mono">
      <thead>
        <tr className="bg-gray-50 text-left text-csm-xs uppercase tracking-wider text-csm-muted">
          <th className="border border-csm-border px-2 py-1 w-1/4">
            {t('audit_log.diff.field')}
          </th>
          {action !== 'INSERT' && (
            <th className="border border-csm-border px-2 py-1 w-[37.5%] text-csm-danger">
              {t('audit_log.diff.old_value')}
            </th>
          )}
          {action !== 'DELETE' && (
            <th className="border border-csm-border px-2 py-1 w-[37.5%] text-csm-success">
              {t('audit_log.diff.new_value')}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        {keys.map(k => (
          <tr key={k} className="even:bg-gray-50">
            <td className="border border-csm-border px-2 py-1 font-semibold text-csm-ink-soft">
              {k}
            </td>
            {action !== 'INSERT' && (
              <td className="border border-csm-border px-2 py-1 text-csm-danger break-all">
                {oldValues?.[k] == null
                  ? <span className="italic text-csm-muted">null</span>
                  : String(oldValues[k])}
              </td>
            )}
            {action !== 'DELETE' && (
              <td className="border border-csm-border px-2 py-1 text-csm-ink break-all">
                {newValues?.[k] == null
                  ? <span className="italic text-csm-muted">null</span>
                  : String(newValues[k])}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Filters
// ---------------------------------------------------------------------------

interface Filters {
  dateFrom:   string;
  dateTo:     string;
  actionType: ActionType;
  entityName: string;
  userId:     string;
}

function defaultFilters(): Filters {
  const now   = new Date();
  const from  = new Date(now);
  from.setDate(from.getDate() - 7);
  return {
    dateFrom:   from.toISOString().slice(0, 10),
    dateTo:     now.toISOString().slice(0, 10),
    actionType: '',
    entityName: '',
    userId:     '',
  };
}

// ---------------------------------------------------------------------------
// CSV export
// ---------------------------------------------------------------------------

function exportCsv(rows: AuditRow[]) {
  const headers = [
    'audit_log_id','log_timestamp','user_email','ip_address',
    'action_type','entity_name','record_id','override_reason',
    'old_values','new_values',
  ];
  const escape = (v: unknown) => {
    const s = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
    return `"${s.replace(/"/g, '""')}"`;
  };
  const lines = [
    headers.join(','),
    ...rows.map(r => [
      r.audit_log_id, r.log_timestamp, r.user_email ?? '', r.ip_address ?? '',
      r.action_type,  r.entity_name,   r.record_id  ?? '', r.override_reason ?? '',
      JSON.stringify(r.old_values ?? null),
      JSON.stringify(r.new_values ?? null),
    ].map(escape).join(',')),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `csm_audit_log_${new Date().toISOString().slice(0,10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export interface AuditLogPageProps { session: AuthSession; }

export function AuditLogPage({ session }: AuditLogPageProps) {
  return (
    <PersonalizationProvider pageCode="CSM.SYSTEM.AUDIT_LOG" session={session}>
      <AuditLogPageInner session={session} />
    </PersonalizationProvider>
  );
}

function AuditLogPageInner({ session }: AuditLogPageProps) {
  const { t }   = useTranslation();
  const [rows, setRows]           = useState<AuditRow[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [page, setPage]           = useState(0);
  const [hasMore, setHasMore]     = useState(false);
  const [filters, setFilters]     = useState<Filters>(defaultFilters);
  const [expanded, setExpanded]   = useState<Set<number>>(new Set());
  const detail = useExpandedRows<number>();
  const { sortKey, sortDir, setSort } =
    useSort<SortKey>('csm.audit.sort', { key: 'log_timestamp', dir: 'desc' });

  // TODO: wire relations — csm_audit_log is a flat forensic log row with no child
  // collections to surface (the field-level diff already renders inline below);
  // the related disclosure renders inert (spacer).
  const buildRelatedGroups = (_id: number): RelatedChildGroup[] => [];
  const hasRelated = (id: number) => buildRelatedGroups(id).some(g => g.items.length > 0);

  const load = useCallback(async (f: Filters, p: number) => {
    setLoading(true);
    setError(null);

    const from = p * PAGE_SIZE;
    const to   = from + PAGE_SIZE - 1;

    let q = apiDb
      .from('csm_audit_log')
      .select(
        'audit_log_id,log_timestamp,user_id,ip_address,' +
        'action_type,entity_name,record_id,' +
        'old_values,new_values,override_reason,' +
        'csm_user_master!csm_audit_log_user_id_fkey(email)'
      )
      .gte('log_timestamp', `${f.dateFrom}T00:00:00Z`)
      .lte('log_timestamp', `${f.dateTo}T23:59:59Z`);

    if (f.actionType) q = q.eq('action_type', f.actionType);
    if (f.entityName) q = q.ilike('entity_name', `%${f.entityName}%`);
    if (f.userId)     q = q.eq('user_id', Number(f.userId));

    q = q.range(from, to)
                  .order('log_timestamp', { ascending: sortDir === 'asc' });

    const { data, error: err } = await q;
    setLoading(false);

    if (err) { setError(err.message); return; }

    type RawAudit = Omit<AuditRow, 'user_email'> & { csm_user_master?: { email?: string } | null };
    const mapped: AuditRow[] = (data ?? []).map((r: RawAudit) => ({
      ...r,
      user_email: r.csm_user_master?.email ?? null,
    }));

    setRows(prev => p === 0 ? mapped : [...prev, ...mapped]);
    setHasMore(mapped.length === PAGE_SIZE);
  }, [sortDir]);

  useEffect(() => {
    setPage(0);
    setRows([]);
    setExpanded(new Set());
    void load(filters, 0);
  }, [filters, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    void load(filters, next);
  };

  const toggleExpanded = (id: number) => {
    setExpanded(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const sorted = useMemo(() => {
    return [...rows].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortKey) {
        case 'log_timestamp': return dir * compareStrings(a.log_timestamp, b.log_timestamp);
        case 'action_type':   return dir * compareStrings(a.action_type,   b.action_type);
        case 'entity_name':   return dir * compareStrings(a.entity_name,   b.entity_name);
        case 'user_email':    return dir * compareStrings(a.user_email ?? '', b.user_email ?? '');
        default:              return 0;
      }
    });
  }, [rows, sortKey, sortDir]);

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('audit_log.title')}
          description={t('audit_log.list.description', 'Field-level history of every change — who changed what, and when.')}
          actions={
            <button
              type="button"
              onClick={() => exportCsv(sorted)}
              disabled={sorted.length === 0}
              className="btn-secondary flex items-center gap-1.5 disabled:opacity-40"
            >
              <Download className="h-3.5 w-3.5" /> {t('audit_log.export_csv')}
            </button>
          }
        />

        <ContentCard className="flex min-h-0 flex-1 flex-col">
          {/* Filter bar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
          <div className="flex items-center gap-1 text-csm-sm text-csm-ink-soft">
            <span>{t('audit_log.filters.from')}</span>
            <DateInput
              value={filters.dateFrom}
              onChange={v => setFilters(f => ({ ...f, dateFrom: v, }))}
              className="h-7 rounded border border-csm-border px-1.5 text-csm-sm focus:border-primary focus:outline-none"
            />
            <span>{t('audit_log.filters.to')}</span>
            <DateInput
              value={filters.dateTo}
              onChange={v => setFilters(f => ({ ...f, dateTo: v }))}
              className="h-7 rounded border border-csm-border px-1.5 text-csm-sm focus:border-primary focus:outline-none"
            />
          </div>

          <select
            value={filters.actionType}
            onChange={e => setFilters(f => ({ ...f, actionType: e.target.value as ActionType }))}
            className="h-7 rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none"
          >
            <option value="">{t('audit_log.filters.all_actions')}</option>
            <option value="INSERT">INSERT</option>
            <option value="UPDATE">UPDATE</option>
            <option value="DELETE">DELETE</option>
            <option value="SECURITY_OVERRIDE">SECURITY OVERRIDE</option>
          </select>

          <input
            type="text"
            placeholder={t('audit_log.filters.entity_placeholder')}
            value={filters.entityName}
            onChange={e => setFilters(f => ({ ...f, entityName: e.target.value }))}
            className="h-7 w-44 rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none"
          />

          <input
            type="number"
            placeholder={t('audit_log.filters.user_id_placeholder')}
            value={filters.userId}
            onChange={e => setFilters(f => ({ ...f, userId: e.target.value }))}
            className="h-7 w-24 rounded border border-csm-border px-2 text-csm-sm focus:border-primary focus:outline-none"
          />

          <button
            type="button"
            onClick={() => setFilters(defaultFilters())}
            className="h-7 rounded border border-csm-border px-2 text-csm-sm
                       text-csm-ink-soft hover:bg-white"
          >
            {t('audit_log.filters.reset')}
          </button>
            <div className="ml-auto flex items-center gap-2">
              <PersonalizationToggle />
            </div>
          </div>

          {/* Grid */}
          <div className="min-h-0 flex-1 overflow-auto">
        {error && (
          <div className="m-3">
            <ErrorBanner
              message={t('audit_log.load_failed', "Couldn't load the audit log.")}
              detail={error}
              retry_label={t('common.retry', 'Retry')}
              on_retry={() => void load(filters, 0)}
              details_label={t('common.details', 'Details')}
            />
          </div>
        )}

        <PersonalizedGrid componentKey="grid" columns={AUDIT_GRID_COLS}>
          {(visibleCols, widths) => {
            const sortKeyFor: Partial<Record<string, SortKey>> = {
              log_timestamp: 'log_timestamp', action_type: 'action_type',
              entity_name: 'entity_name', user_email: 'user_email',
            };
            return (
              <table className="w-full min-w-[900px] border-collapse text-csm-sm">
                <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_#E5E7EB]">
                  <tr className="text-left text-csm-xs uppercase tracking-wider text-csm-muted">
                    <th className="w-7 border-b border-csm-border px-2 py-2" title={t('audit_log.related.header', 'Related records')} />
                    <th className="w-6 border-b border-csm-border px-2 py-2" />
                    {visibleCols.map(col => {
                      const sk = sortKeyFor[col.key];
                      if (sk) return (
                        <SortableHeader<SortKey>
                          key={col.key}
                          label={col.header}
                          sortKey={sk}
                          currentKey={sortKey}
                          currentDir={sortDir}
                          onSort={setSort}
                          className="border-b border-csm-border px-2 py-2"
                        />
                      );
                      return (
                        <th key={col.key} className="border-b border-csm-border px-2 py-2"
                            style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.header}
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(row => {
                    const isOpen = expanded.has(row.audit_log_id);
                    const hasDiff = row.old_values || row.new_values || row.override_reason;
                    return (
                      <Fragment key={row.audit_log_id}>
                        <tr
                          onClick={() => hasDiff && toggleExpanded(row.audit_log_id)}
                          className={[
                            'border-b border-csm-border',
                            hasDiff ? 'cursor-pointer hover:bg-csm-surface-alt' : '',
                            isOpen  ? 'bg-blue-50' : '',
                          ].join(' ')}
                        >
                          <td className="px-1 py-1.5 text-center align-middle" onClick={e => e.stopPropagation()}>
                            <RelatedExpandButton
                              open={detail.isOpen(row.audit_log_id)}
                              hasChildren={hasRelated(row.audit_log_id)}
                              onToggle={() => detail.toggle(row.audit_log_id)}
                              expandLabel={t('audit_log.related.expand', 'Show related records')}
                              collapseLabel={t('audit_log.related.collapse', 'Hide related')}
                              tooltip={t('audit_log.related.tooltip', 'Show related records')}
                            />
                          </td>
                          <td className="px-2 py-1.5 text-csm-muted text-center">
                            {hasDiff ? (isOpen ? '▼' : '▶') : ''}
                          </td>
                          {visibleCols.map(col => (
                            <td key={col.key} className="px-2 py-1.5"
                                style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                              {col.key === 'log_timestamp' && <span className="font-mono text-csm-xs text-csm-ink-soft whitespace-nowrap">{new Date(row.log_timestamp).toLocaleString()}</span>}
                              {col.key === 'action_type'   && <ActionBadge action={row.action_type} />}
                              {col.key === 'entity_name'   && <span className="font-mono text-csm-ink">{row.entity_name}</span>}
                              {col.key === 'record_id'     && <span className="font-mono text-csm-xs text-csm-ink-soft">{row.record_id ?? '—'}</span>}
                              {col.key === 'user_email'    && <span className="text-csm-ink-soft">{row.user_email ?? '—'}</span>}
                              {col.key === 'ip_address'    && <span className="font-mono text-csm-xs text-csm-muted">{row.ip_address ?? '—'}</span>}
                            </td>
                          ))}
                        </tr>

                        {detail.isOpen(row.audit_log_id) && (
                          <tr key={`${row.audit_log_id}-related`}>
                            <td colSpan={visibleCols.length + 2} className="border-b border-csm-border bg-csm-surface-alt/40 px-3 py-3" onClick={e => e.stopPropagation()}>
                              <RelatedChildrenPanel groups={buildRelatedGroups(row.audit_log_id)} session={session} />
                            </td>
                          </tr>
                        )}

                        {isOpen && hasDiff && (
                          <tr key={`${row.audit_log_id}-detail`} className="bg-blue-50">
                            <td colSpan={visibleCols.length + 2} className="px-6 pb-3 pt-1">
                              {row.override_reason ? (
                                <div className="mb-2 rounded border border-csm-warning-border bg-csm-warning-soft
                                                px-3 py-2 text-csm-sm text-csm-warning">
                                  <span className="font-semibold">{t('audit_log.diff.reason')}: </span>
                                  {row.override_reason}
                                </div>
                              ) : null}
                              {(row.old_values || row.new_values) && (
                                <DiffTable
                                  oldValues={row.old_values}
                                  newValues={row.new_values}
                                  action={row.action_type}
                                />
                              )}
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}

                  {!loading && sorted.length === 0 && (
                    <tr>
                      <td colSpan={visibleCols.length + 2} className="px-4 py-6">
                        <EmptyState variant="no-match" title={t('audit_log.no_results')} />
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            );
          }}
        </PersonalizedGrid>

        {loading && (
          sorted.length === 0
            ? <SkeletonRows rows={8} />
            : (
              <div className="px-4 py-3 text-csm-sm text-csm-muted">
                {t('common.loading')}
              </div>
            )
        )}

        {hasMore && !loading && (
          <div className="flex justify-center px-4 py-3">
            <button
              type="button"
              onClick={loadMore}
              className="h-8 rounded border border-csm-border px-4 text-csm-sm
                         text-csm-ink-soft hover:bg-csm-surface-alt"
            >
              {t('audit_log.load_more', { count: PAGE_SIZE })}
            </button>
          </div>
        )}
          </div>

          {/* Footer summary */}
          <div className="flex shrink-0 items-center border-t border-csm-line bg-csm-surface2 px-3 py-1.5 text-csm-xs text-csm-muted">
            {t('audit_log.row_count', { count: sorted.length })}
            {hasMore ? ` (${t('audit_log.more_available')})` : ''}
          </div>
        </ContentCard>
      </div>
    </div>
  );
}
